import { BadgeColorScheme } from '@agrotoken-tech/ui';
import {
  creditorCropPledgeStatusBadgeColors,
  creditorPledgeSectionStatusBadgeColors,
  debtorCropPledgeStatusBadgeColors,
  debtorPledgeSectionStatusBadgeColors,
} from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import {
  CROP_PLEDGE_USER_ROLES,
  type CropPledgeStatusSummaryResponse,
  type CropPledgeUserRole,
  PLEDGE_SECTION_STATUSES,
  PLEDGE_STATUSES,
  type PledgeSectionStatus,
  PledgeStatus,
} from '@bcba/types';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { getCropPledgeSectionStatuses } from '../../../../services/crop-pledge/get-crop-pledge-section-statuses';
import { CropPledgeAsideTabData } from '../../shared/CropPledgeAside';
import CreditorSection from '../CreditorSection';
import { DebtorSection } from '../DebtorSection';
import DocumentationSection from '../DocumentationSection';
import PledgeFarmInformation from '../FarmInformationSection/PledgeFarmInformationSection';
import PledgeInsuranceInformation from '../InsuranceInformation';
import PledgeInformation from '../PledgeInformation';

type CropPledgeContextType = {
  tabsDataAside: {
    title: string;
    content: React.ReactNode;
    status: PledgeSectionStatus;
  }[];
  sectionTitle: string;
  setSectionTitle: React.Dispatch<React.SetStateAction<string>>;
  cropPledgeStatus?: CropPledgeStatusSummaryResponse;
  role: CropPledgeUserRole;
  cropPledgeStatusBadgeColors: Record<
    (typeof PLEDGE_STATUSES)[keyof typeof PLEDGE_STATUSES],
    BadgeColorScheme
  >;
  cropPledgeSectionsStatusBadgeColors: Record<
    (typeof PLEDGE_SECTION_STATUSES)[keyof typeof PLEDGE_SECTION_STATUSES],
    BadgeColorScheme
  >;
  refetchCropPledgeStatus: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<CropPledgeStatusSummaryResponse, Error>>;
  totalEstimatedProductionValueInUsd: string;
  isPledgeInAnEditableStage: boolean;
};

const CropPledgeContext = createContext<CropPledgeContextType | undefined>(
  undefined,
);
type CropPledgeProviderProps = {
  children: ReactNode;
};

export const CropPledgeProvider: React.FC<CropPledgeProviderProps> = ({
  children,
}) => {
  const [sectionTitle, setSectionTitle] = useState('');
  const { t } = useTranslation();
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';

  const { data: cropPledgeStatus, refetch: refetchCropPledgeStatus } = useQuery(
    {
      queryKey: ['crop-pledge-status', cropPledgeUuid],
      queryFn: () => getCropPledgeSectionStatuses(cropPledgeUuid),
      enabled: Boolean(cropPledgeUuid),
    },
  );

  const mapStatus = (status?: PledgeSectionStatus): PledgeSectionStatus => {
    if (!status) return PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD;
    return status;
  };

  const tabsDataAside = useMemo(() => {
    const statusSections = cropPledgeStatus?.sections;

    const tabs: CropPledgeAsideTabData[] = [
      {
        title: t('cropPledge.section.fieldInfo.label'),
        content: <PledgeFarmInformation />,
        status: mapStatus(statusSections?.farm?.status),
      },
      {
        title: t('cropPledge.section.debtorInfo.title.label'),
        content: <DebtorSection />,
        status: mapStatus(statusSections?.debtor.status),
      },
      {
        title: t('cropPledge.section.creditorInfo.label'),
        content: <CreditorSection />,
        status: mapStatus(statusSections?.creditor.status),
      },
      {
        title: t('cropPledge.section.document.label'),
        content: <DocumentationSection />,
        status: mapStatus(statusSections?.documents.status),
        isDisabled: !statusSections?.documents.enabled,
      },
      {
        title: t('cropPledge.section.cropPledgeInfo.label'),
        content: <PledgeInformation />,
        status: mapStatus(statusSections?.pledge.status),
        isDisabled: !statusSections?.pledge.enabled,
      },
      {
        title: t('cropPledge.section.insuranceInfo.label'),
        content: <PledgeInsuranceInformation />,
        status: mapStatus(statusSections?.insurance.status),
        isDisabled: !statusSections?.insurance.enabled,
      },
    ];
    return tabs;
  }, [cropPledgeStatus, t]);

  useEffect(() => {
    setSectionTitle(
      t('cropPledge.page.title.label').replace(
        '{{cropPledgeNumber}}',
        cropPledgeStatus?.internalIdentifier ?? '',
      ),
    );
  }, [cropPledgeStatus?.internalIdentifier, t]);

  const cropPledgeStatusBadgeColors =
    cropPledgeStatus?.role === CROP_PLEDGE_USER_ROLES.CREDITOR
      ? creditorCropPledgeStatusBadgeColors
      : debtorCropPledgeStatusBadgeColors;

  const cropPledgeSectionsStatusBadgeColors =
    cropPledgeStatus?.role === CROP_PLEDGE_USER_ROLES.CREDITOR
      ? creditorPledgeSectionStatusBadgeColors
      : debtorPledgeSectionStatusBadgeColors;

  const totalEstimatedProductionValueInUsd =
    cropPledgeStatus?.totalEstimatedProductionValueInUsd ?? '';

  const readOnlyPledgeStatuses: PledgeStatus[] = [
    PLEDGE_STATUSES.PENDING_REGISTRATION_BLOCKCHAIN,
    PLEDGE_STATUSES.REGISTERED,
    PLEDGE_STATUSES.REJECTED,
    PLEDGE_STATUSES.RELEASED,
    PLEDGE_STATUSES.PENDING_RELEASE,
  ];
  const isPledgeInAnEditableStage =
    !cropPledgeStatus?.statuses?.some((status) =>
      readOnlyPledgeStatuses.includes(status),
    ) ?? false;

  return (
    <CropPledgeContext.Provider
      value={{
        tabsDataAside,
        sectionTitle,
        setSectionTitle,
        cropPledgeStatus,
        role: cropPledgeStatus?.role ?? CROP_PLEDGE_USER_ROLES.DEBTOR,
        cropPledgeStatusBadgeColors,
        cropPledgeSectionsStatusBadgeColors,
        refetchCropPledgeStatus,
        totalEstimatedProductionValueInUsd,
        isPledgeInAnEditableStage,
      }}
    >
      {children}
    </CropPledgeContext.Provider>
  );
};

export const useCropPledgeContext = (): CropPledgeContextType => {
  const context = useContext(CropPledgeContext);
  if (context === undefined) {
    throw new Error(
      'useCropPledgeContext must be used within a CropPledgeProvider',
    );
  }
  return context;
};
